import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Loader2 } from "lucide-react";
import { sendOtp, verifyOtp } from "@/lib/api";
import { toast } from "sonner";

export default function OtpModal({ open, phone, onClose, onVerified }) {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sentOnce, setSentOnce] = useState(false);
  const [channel, setChannel] = useState("sms");
  const [emailMasked, setEmailMasked] = useState("");
  const firstSend = useRef(false);

  const doSend = async () => {
    setSending(true);
    try {
      const data = await sendOtp(phone);
      const ch = data?.channel || "sms";
      setChannel(ch);
      setEmailMasked(data?.email_masked || "");
      setSentOnce(true);
      toast.success(ch === "email" ? "Te enviamos un código a tu correo." : "Te enviamos un código por SMS.");
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error(e?.response?.data?.detail || "No pudimos enviar el código. Intenta de nuevo.", { duration: Infinity });
      } else {
        toast.error(e?.response?.data?.detail || "No pudimos enviar el código. Intenta de nuevo.");
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (open && !firstSend.current) {
      firstSend.current = true;
      doSend();
    }
    if (!open) {
      firstSend.current = false;
      setCode("");
      setSentOnce(false);
    }
    // eslint-disable-next-line
  }, [open]);

  const doVerify = async () => {
    if (code.replace(/\D/g, "").length < 4) {
      toast.error("Ingresa el código que recibiste.");
      return;
    }
    setVerifying(true);
    try {
      const res = await verifyOtp(phone, code);
      if (res.approved) {
        onVerified();
      } else {
        toast.error(res.detail || "El código es incorrecto o expiró.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos validar el código. Intenta de nuevo.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="otp-modal" className="max-w-md border-af-border-dark"
        style={{ background: "var(--af-black-soft)", color: "#fff" }}>
        <div className="text-center">
          <div className="mx-auto rounded-full flex items-center justify-center mb-4"
            style={{ width: 56, height: 56, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
            <ShieldCheck className="text-af-orange-core" size={28} />
          </div>
          <DialogTitle className="text-white font-bold text-xl">Verifica tu identidad</DialogTitle>
          <p className="text-white/65 text-sm mt-2">
            {channel === "email" ? (
              <>Enviamos un código a tu correo{emailMasked ? <> <span className="text-white font-medium">{emailMasked}</span></> : ""}. Ingrésalo para continuar.</>
            ) : (
              <>Enviamos un código por SMS al número <span className="text-white font-medium">{phone}</span>. Ingrésalo para continuar.</>
            )}
          </p>

          <input
            data-testid="otp-input"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
            inputMode="numeric"
            placeholder="Código de verificación"
            className="af-input text-center tracking-[0.4em] text-lg mt-6"
            autoFocus
          />

          <button data-testid="otp-verify-btn" onClick={doVerify} disabled={verifying || sending}
            className="af-btn-primary w-full mt-4">
            {verifying ? <><Loader2 className="animate-spin" size={18} /> Verificando…</> : "Confirmar código"}
          </button>

          <button data-testid="otp-resend-btn" onClick={doSend} disabled={sending}
            className="text-af-orange-core text-sm mt-4 hover:underline disabled:opacity-50">
            {sending ? "Enviando…" : sentOnce ? "Reenviar código" : "Enviar código"}
          </button>

          <p className="text-white/40 text-xs mt-4">
            Por seguridad limitamos los intentos y reenvíos. Nunca compartas tu código con nadie.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
